import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Container, Chip, LinearProgress, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { useProject } from '../../../hooks/ProjectContext';
import { exportToCsv } from './RegistryUtils';

const COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'phase_inclusion', label: 'Phase' },
  { key: 'land_use', label: 'Land Use' },
  { key: 'owner_name', label: 'Owner' },
  { key: 'feasibility_status', label: 'Status' },
];

export default function SitesRegistryPage() {
  const { selectedProject } = useProject();
  const [sites, setSites] = useState([]);
  const projectId = selectedProject?.id;

  const load = useCallback(() => {
    if (!projectId) return;
    fetch(`/v1/sites-feasibility?projectId=${projectId}`)
      .then(r => r.ok ? r.json() : []).then(setSites).catch(() => setSites([]));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (siteId, fields) => {
    await fetch(`/v1/sites/${siteId}/feasibility`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    load();
  };

  return (
    <>
      <Helmet><title>Sites Registry | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5">Sites Registry</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}
            onClick={() => exportToCsv('sites.csv', COLUMNS, sites)}>
            Export CSV
          </Button>
        </Stack>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(c => (
                  <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: 12 }}>{c.label}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.map(site => {
                const total = 8;
                const done = (site.claims?.done || 0) + (site.claims?.verified || 0);
                const pct = Math.round(done / total * 100);
                return (
                  <TableRow key={site.id} hover>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{site.code}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{site.name}</TableCell>
                    <TableCell>
                      <Select size="small" value={site.phase_inclusion || 'current'}
                        onChange={e => handleUpdate(site.id, { phase_inclusion: e.target.value })}
                        sx={{ fontSize: 11, height: 26 }}>
                        <MenuItem value="current">Current</MenuItem>
                        <MenuItem value="expansion">Expansion</MenuItem>
                        <MenuItem value="candidate">Candidate</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select size="small" value={site.land_use || ''} displayEmpty
                        onChange={e => handleUpdate(site.id, { land_use: e.target.value })}
                        sx={{ fontSize: 11, height: 26 }}>
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value="cropland">Cropland</MenuItem>
                        <MenuItem value="grassland">Grassland</MenuItem>
                        <MenuItem value="mixed">Mixed</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{site.owner_name || '—'}</TableCell>
                    <TableCell>
                      <Chip label={site.feasibility_status || 'empty'} size="small"
                        color={site.feasibility_status === 'done' ? 'success' : site.feasibility_status === 'in_progress' ? 'warning' : 'default'}
                        variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                        <Typography variant="caption" sx={{ fontSize: 10 }}>{done}/{total}</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
}
